<script setup>
import { useAnalysisSajuContext, getHarmonyBaseId } from '@/composables/saju-insight/useAnalysisSajuContext';
const context = useAnalysisSajuContext();
let alive = true;
onBeforeUnmount(() => { alive = false; });
const isCurrent = () => alive && (!context || context.isActive());
const filterCurrentLogs = (logs) => (logs || []).filter((log) =>
  !context || getHarmonyBaseId(log) === Number(context.saju.value?.guestUserId));

import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import { getAnalysisContentAPI, getAnalysisLogAPI, postAnalysisLogAPI } from '@/api/AnalysisApi';
import ConfirmComponentPay from '@/components/saju-insight/common/confirmComponent.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import AlertComponent from '@/components/common/Alert.vue';
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import { addContentLogAPI } from '@/api/logServiceApi';
import { DEV_MODE } from '@/config/dev-mode';
import { fetchHarmonyServiceAPI } from '@/api/baseServiceApi';
import { getDetail as getGuestUserDetail } from '@/api/UserGroup';
import { useTicketProduct } from '@/composables/saju-insight/useTicketProduct';
import { formatNumber } from '@/composables/functions';

const ANALYSIS_TYPE_ID = 38;
const route = useRoute();
const router = useRouter();

const props = defineProps({
  harmonyTicketData: {
    type: Object,
    default: null,
  },
  harmonyLogData: {
    type: Array,
    default: () => [],
  },
  hasPermission: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['goPopAnalysis', 'payment-success']);

const paymentsStore = usePaymentsStore();
const analysisStore = useAnalysisStore();
const primarySajuStore = usePrimarySajuStore();

/** 궁합목록 카테고리 */
const HARMONY_CATEGORIES = [
  { id: 'lover', label: '연인', emoji: '💖' },
  { id: 'friend', label: '친구', emoji: '🤝' },
  { id: 'business', label: '비즈니스', emoji: '💼' },
  { id: 'family', label: '가족', emoji: '🏠' },
];

const selectedCategory = ref('lover');

/** 카테고리 탭 드래그 스크롤 */
const categoryTabsRef = ref(null);
const isDragging = ref(false);
let dragStartX = 0;
let scrollStartX = 0;

const onTabsMouseDown = (e) => {
  const el = categoryTabsRef.value;
  if (!el) return;
  isDragging.value = false;
  dragStartX = e.pageX;
  scrollStartX = el.scrollLeft;

  const onMouseMove = (ev) => {
    const dx = ev.pageX - dragStartX;
    if (Math.abs(dx) > 3) isDragging.value = true;
    el.scrollLeft = scrollStartX - dx;
  };

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    if (isDragging.value) {
      setTimeout(() => { isDragging.value = false; }, 0);
    }
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
};

const onTabsTouchStart = (e) => {
  const el = categoryTabsRef.value;
  if (!el) return;
  isDragging.value = false;
  const touch = e.touches[0];
  dragStartX = touch.pageX;
  scrollStartX = el.scrollLeft;

  const onTouchMove = (ev) => {
    const dx = ev.touches[0].pageX - dragStartX;
    if (Math.abs(dx) > 3) isDragging.value = true;
    el.scrollLeft = scrollStartX - dx;
  };

  const onTouchEnd = () => {
    el.removeEventListener('touchmove', onTouchMove);
    el.removeEventListener('touchend', onTouchEnd);
    if (isDragging.value) {
      setTimeout(() => { isDragging.value = false; }, 0);
    }
  };

  el.addEventListener('touchmove', onTouchMove, { passive: true });
  el.addEventListener('touchend', onTouchEnd);
};

/** 궁합 목록 데이터 (카테고리별 API 조회) */
const CATEGORY_API_MAP = {
  lover: 'LOVER',
  friend: 'FRIEND',
  business: 'BUSINESS',
  family: 'FAMILY',
};

const { analysisLogByType } = storeToRefs(analysisStore);
const isCategoryLoading = ref(false);

/** 전체 typeId=38 로그 (props로 카테고리 무관 전체가 들어옴) */
const allHarmonyLogs = computed(() => filterCurrentLogs(props.harmonyLogData));

/** 카테고리별 구매 여부 맵 */
const categoryPurchaseMap = computed(() => {
  const map = { lover: false, friend: false, business: false, family: false };
  allHarmonyLogs.value.forEach((log) => {
    const entry = Object.entries(CATEGORY_API_MAP).find(([, api]) => api === log.category);
    if (entry) map[entry[0]] = true;
  });
  return map;
});

/** 전체 카테고리 미구매 여부 */
const hasAnyPurchase = computed(() =>
  Object.values(categoryPurchaseMap.value).some(Boolean),
);

/** 카테고리별 로그 데이터 (store 캐시 → props 전체 fallback) */
const filteredLogData = computed(() => {
  if (!selectedCategory.value) return [];
  const apiCategory = CATEGORY_API_MAP[selectedCategory.value] || 'LOVER';
  const cacheKey = `${ANALYSIS_TYPE_ID}_${apiCategory}`;
  const cached = analysisLogByType.value[cacheKey];
  if (cached?.length) return filterCurrentLogs(cached);
  return allHarmonyLogs.value.filter((log) => log.category === apiCategory);
});

/** 카테고리 변경 시 해당 카테고리로 API 호출
 *  - 사용자 클릭은 :disabled 속성이 차단하므로 별도 가드 없음
 *  - 결제 데이터(harmonyPaymentData) 변경 watch에서 미구매 카테고리도 활성 탭으로 표시되도록 허용
 */
const onChangeCategory = async (catId) => {
  if (isDragging.value) return;
  selectedCategory.value = catId;
  const apiCategory = CATEGORY_API_MAP[catId] || 'LOVER';
  const cacheKey = `${ANALYSIS_TYPE_ID}_${apiCategory}`;
  if (!analysisLogByType.value[cacheKey]) {
    isCategoryLoading.value = true;
  }
  const guestUserId = route.params?.id || route.query?.id || null;
  if (isCurrent()) await analysisStore.getAnalysisLog(guestUserId, ANALYSIS_TYPE_ID, apiCategory);
  isCategoryLoading.value = false;
};

/** cover에서 전달된 결제 데이터 (category, memberGuestUserIds, persons) */
const { harmonyPaymentData } = storeToRefs(analysisStore);

/** 결제 카테고리 또는 목록 카드 클릭 시 궁합목록 탭 자동 선택 + API 호출 */
watch(
  () => harmonyPaymentData.value?.category || analysisStore.harmonyMatchedLog?.category,
  (apiCategory) => {
    if (apiCategory) {
      const tabId = apiCategory.toLowerCase();
      if (CATEGORY_API_MAP[tabId]) {
        onChangeCategory(tabId);
      }
    }
  },
  { immediate: true },
);

const formatListDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}.${m}.${d}`;
};

/** ===== 분석 타입 API에서 가격/옵션 정보 가져오기 ===== */
const analysisTypeData = ref(null);
const loadingPrice = ref(false);

/** 추가해석 타입별 데이터 (월운: 39, 세운: 40) */
const extraTypeData = ref({ monthly: null, yearly: null });

const fetchAnalysisType = async () => {
  if (analysisTypeData.value) return; // 이미 로드 완료된 경우 스킵
  loadingPrice.value = true;
  try {
    const data = await getAnalysisContentAPI({ analysisTypeId: ANALYSIS_TYPE_ID });
    analysisTypeData.value = data;
    DEV_MODE && console.log('[궁합] analysisType 데이터:', data);

    // children에서 월운/세운 typeId를 추출하여 개별 조회
    const children = data?.children || [];
    const yearlyChild = children.find((c) => c.code === 'COMPAT_YEARLY');
    const monthlyChild = children.find((c) => c.code === 'COMPAT_MONTHLY');

    const extraFetches = [];
    if (monthlyChild?.analysisTypeId) {
      extraFetches.push(
        getAnalysisContentAPI({ analysisTypeId: monthlyChild.analysisTypeId })
          .then((d) => { extraTypeData.value.monthly = d; })
          .catch(() => {}),
      );
    }
    if (yearlyChild?.analysisTypeId) {
      extraFetches.push(
        getAnalysisContentAPI({ analysisTypeId: yearlyChild.analysisTypeId })
          .then((d) => { extraTypeData.value.yearly = d; })
          .catch(() => {}),
      );
    }
    await Promise.all(extraFetches);
    DEV_MODE && console.log('[궁합] 추가해석 타입 데이터:', extraTypeData.value);
  } catch (e) {
    DEV_MODE && console.error('[궁합] analysisType 조회 실패:', e);
  } finally {
    loadingPrice.value = false;
  }
};

onMounted(() => {
  fetchAnalysisType();
  // 초기 카테고리 로드 (watch immediate에서 처리되지 않은 경우 대비)
  if (!harmonyPaymentData.value?.category && !analysisStore.harmonyMatchedLog?.category) {
    onChangeCategory(selectedCategory.value);
  }
});

/** CoverPanelHarmony에서 카테고리+멤버 기반으로 판별한 결제 여부를 store에서 참조 */
const isPurchased = computed(() => analysisStore.harmonyIsPurchased);
/** 구독이 이 조합을 커버: 구독중 && base(sortOrder 최상위, 본인 자리)가 내사주.
 *  BE 구독 커버가 base 기준이라 FE도 base로 판정. */
const subscriptionCovers = computed(() => {
  if (!paymentsStore.isSubscribed) return false;
  const persons = harmonyPaymentData.value?.persons || [];
  if (!persons.length) return false;
  const base = [...persons].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))[0];
  return primarySajuStore.isMySaju(Number(base?.guestUserId));
});
/** 표시용 권한: 구매 OR 구독 커버 → 전체 열람 가능 */
const canViewFull = computed(() => isPurchased.value || subscriptionCovers.value);

/** content1 결과보기 클릭 → 결제 팝업 트리거 감시 */
watch(
  () => analysisStore.harmonyPaymentPopupTrigger,
  (val) => {
    if (val > 0 && !isPurchased.value) {
      openPaymentPopup();
    }
  },
);

/** 선택된 인원수에 맞는 옵션 찾기 */
const matchedOption = computed(() => {
  const options = analysisTypeData.value?.options;
  const paymentData = harmonyPaymentData.value;

  if (!options?.length || !paymentData) return null;

  const memberCount = paymentData.memberGuestUserIds?.length || 2;
  const found = options.find((opt) => opt.memberCount === memberCount) || null;

  DEV_MODE &&
    console.log('[궁합] matchedOption 계산:', {
      memberCount,
      optionsCount: options.length,
      found: found ? { optionId: found.optionId, pointPrice: found.pointPrice } : null,
    });

  return found;
});

/** 동적 가격 (API 기반) — matchedOption 우선, 없으면 인원수 기반 계산 */
const pointPrice = computed(() => {
  if (matchedOption.value?.pointPrice) return matchedOption.value.pointPrice;

  // matchedOption이 아직 없으면 (API 로딩 중 등) 인원수 기반 fallback
  const memberCount = harmonyPaymentData.value?.memberGuestUserIds?.length || 2;
  const basePrice = analysisTypeData.value?.pointPrice ?? 1000;
  const extraPerPerson = 300; // 추가 인원당 300P
  return basePrice + Math.max(0, memberCount - 2) * extraPerPerson;
});

/** 선택 인원 옵션의 정가(할인 전) */
const pointBasePrice = computed(() => {
  return matchedOption.value?.pointBasePrice ?? analysisTypeData.value?.pointBasePrice ?? null;
});

/** 정가 대비 할인율 표시 (예: "25% 할인") — 정가가 결제가보다 클 때만 노출 */
const discountText = computed(() => {
  const base = pointBasePrice.value;
  const price = pointPrice.value;
  if (!base || base <= price) return '';
  return `${Math.round(((base - price) / base) * 100)}% 할인`;
});

const currentPoints = computed(() => {
  return Number(paymentsStore?.points) || 0;
});

/** 인원수별 분석권 상세 */
const harmonyTickets = computed(() => paymentsStore.harmonyTicketDetails);

/** 현재 선택 인원수 */
const selectedMemberCount = computed(() => harmonyPaymentData.value?.memberGuestUserIds?.length || 2);

/** 현재 선택 인원수에 매칭되는 분석권 정보 */
const matchedTicketInfo = computed(() => {
  return harmonyTickets.value[`members${selectedMemberCount.value}`] || { count: 0, ticketId: null };
});

/** 선택 인원수에 매칭되는 분석권 표시 텍스트 (예: "2인 1건", 0건이면 "0건") */
const matchedTicketDisplayText = computed(() => {
  const mc = selectedMemberCount.value;
  const count = matchedTicketInfo.value.count;
  return count > 0 ? `${mc}인 ${count}건` : '0건';
});

const shortagePoints = computed(() => {
  const diff = pointPrice.value - currentPoints.value;
  return diff > 0 ? diff : 0;
});

/** 선택 인원 요약 텍스트 */
const memberSummary = computed(() => {
  if (!harmonyPaymentData.value?.persons) return '';
  const truncate = (str) => str && str.length > 4 ? str.slice(0, 4) + '…' : str;
  return harmonyPaymentData.value.persons.map((p) => truncate(p.name || p.guestUserName)).join(' & ');
});

/** 결제 팝업 상태 */
const showPaymentPopup = ref(false);
const buyType = ref('');
const isPointActive = computed(() => currentPoints.value >= pointPrice.value);
const isTicketActive = computed(() => matchedTicketInfo.value.count > 0);

const { fetchTicketProductByTypeId } = useTicketProduct();
/** 분석권 업셀 카드용 상품 정보 ({ boardId, articleId, perUnitPrice, unit, discountPct }) */
const ticketProduct = ref(null);

const handleBuyPoint = () => {
  if (!isPointActive.value) return;
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '포인트' });
  buyType.value = 'point';
};

/** 분석권 구매 이동 확인 팝업 ref */
const ticketConfirmRef = ref(null);

/** 업셀 카드 클릭 → 바로 이동하지 않고 작성 내용 초기화 안내 확인 팝업 표시 */
const goTicketProduct = () => {
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '분석권상품' });
  ticketConfirmRef.value?.openConfirmDialog();
};

/** 확인(네) 클릭 → 현재 인원수에 맞는 궁합 분석권 상품 상세로 이동 (없으면 분석권 목록) */
const moveToTicketProduct = () => {
  if (ticketProduct.value?.articleId) {
    router.push(
      `/purchase/ticket-detail/${ticketProduct.value.boardId}/${ticketProduct.value.articleId}`,
    );
  } else {
    router.push('/purchase/tickets');
  }
  closePaymentPopup();
};

const openPaymentPopup = async () => {
  addContentLogAPI({ contentType: 'ap_harmony_btn_start_detail' });
  await Promise.all([paymentsStore.getOwnPoint(), paymentsStore.fetchTicketCount()]);

  // 포인트 부족 + 분석권 없음 → 포인트 충전 팝업 표시
  if (!isPointActive.value && !isTicketActive.value) {
    chargePopupAnalysisType.value = {
      pointPrice: pointPrice.value,
      pointBasePrice:
        matchedOption.value?.pointBasePrice ||
        analysisTypeData.value?.pointBasePrice ||
        pointPrice.value,
    };
    showChargePopup.value = true;
    showCategoryPayConfirm.value = false;
    pendingCategoryPayment.value = null;
    return;
  }

  // 분석권이 있으면 결제 방법 선택 UI를 건너뛰고 바로 확인 팝업
  if (isTicketActive.value) {
    buyType.value = 'ticket';
    goConfirm();
    return;
  }

  buyType.value = 'point';
  showPaymentPopup.value = true;
  // 현재 인원수에 맞는 할인 분석권 업셀 상품을 비동기로 조회
  ticketProduct.value = null;
  fetchTicketProductByTypeId(ANALYSIS_TYPE_ID, {
    memberCount: selectedMemberCount.value,
  }).then((product) => {
    ticketProduct.value = product;
  });
};

const closePaymentPopup = () => {
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '닫기' });
  showPaymentPopup.value = false;
  showCategoryPayConfirm.value = false;
  pendingCategoryPayment.value = null;
};

/** 포인트 충전 팝업 (포인트 부족 + 분석권 없을 때) */
const showChargePopup = ref(false);
const chargePopupAnalysisType = ref(null);

const handleCloseChargePopup = () => {
  showChargePopup.value = false;
};

const handleGoCharge = (shortageAmount) => {
  showChargePopup.value = false;
  router.push({
    path: '/purchase/points',
    query: {
      next: route.fullPath,
      shortageAmount,
    },
  });
};

/** 결제 확인 다이얼로그 */
const dialogConfirm = ref(null);
const dialogAlert = ref(null);
const dialogTitle = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref(null);
const dialogDoUrl = ref(null);
const warningTxt = ref('');

const goConfirm = () => {
  if (!buyType.value) return;
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '확인' });
  showPaymentPopup.value = false;
  showCategoryPayConfirm.value = false;
  // pendingCategoryPayment는 결제 완료/실패 후에 정리 (onConfirmPayment에서 카테고리 참조 필요)

  if (buyType.value === 'ticket' && isTicketActive.value) {
    dialogTitle.value = '분석권을 사용하시겠습니까?';
    dialogTitle2.value = '';
    warningTxt.value = `낭만궁합 - 분석권 1건 사용`;
  } else {
    dialogTitle.value = '선택한 결제방법으로';
    dialogTitle2.value = '낭만궁합을 시작합니다.';
    warningTxt.value =
      buyType.value === 'point' ? `포인트 ${pointPrice.value.toLocaleString()} P 사용` : '분석권 1건 사용';
  }
  dialogText.value = '사용';
  dialogNextUrl.value = onConfirmPayment;
  dialogDoUrl.value = onConfirmPayment;
  dialogConfirm.value.openConfirmDialog();
};

/** 결제 처리 — 실제 API 호출 */
const isPaymentProcessing = ref(false);

const onConfirmPayment = async () => {
  if (!isCurrent() || isPaymentProcessing.value) return;
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '결제확정' });
  isPaymentProcessing.value = true;

  try {
    const paymentData = harmonyPaymentData.value;
    if (context && Number(paymentData?.memberGuestUserIds?.[0]) !== Number(context.saju.value?.guestUserId)) {
      throw new Error('현재 사주를 기준으로 궁합을 다시 선택해주세요.');
    }
    if (!paymentData) {
      throw new Error('결제 데이터가 없습니다. 궁합 서비스 선택을 다시 해주세요.');
    }

    // 카테고리 재구매 플로우: pendingCategoryPayment가 있으면 해당 카테고리 사용
    const effectiveCategory = pendingCategoryPayment.value
      ? CATEGORY_API_MAP[pendingCategoryPayment.value.id]
      : paymentData.category;

    const params = {
      guestUserId: paymentData.memberGuestUserIds[0],
      analysisTypeId: ANALYSIS_TYPE_ID,
      paymentMethod: buyType.value === 'point' ? 'point' : 'ticket',
      optionId: matchedOption.value?.optionId,
      category: effectiveCategory,
      memberGuestUserIds: paymentData.memberGuestUserIds,
    };

    DEV_MODE && console.log('[궁합] 결제 요청 params:', params);

    await postAnalysisLogAPI(params);
    if (!isCurrent()) return;

    // 분석권 결제 성공 시 서버 반영 지연 대비 낙관적 차감
    if (buyType.value === 'ticket') {
      paymentsStore.decrementHarmonyTicket(paymentData.memberGuestUserIds.length);
    }

    dialogTitle.value = '성공적으로 결제되었습니다.';
    dialogText.value = '';
    dialogNextUrl.value = onPaymentSuccess;
    dialogAlert.value.openAlertDialog();
  } catch (e) {
    DEV_MODE && console.error('[궁합] 결제 실패:', e);
    pendingCategoryPayment.value = null;
    dialogTitle.value = '결제에 실패했습니다.';
    dialogText.value = '';
    dialogNextUrl.value = null;
    dialogAlert.value.openAlertDialog();
  } finally {
    isPaymentProcessing.value = false;
  }
};

const onPaymentSuccess = async () => {
  if (!isCurrent()) return;
  emit('payment-success', true);

  // 카테고리 재구매 플로우: pendingCategoryPayment가 있으면 해당 카테고리 사용
  const paymentData = harmonyPaymentData.value;
  const paymentCategory = pendingCategoryPayment.value
    ? CATEGORY_API_MAP[pendingCategoryPayment.value.id]
    : paymentData?.category;
  const selectedIds = paymentData?.memberGuestUserIds?.map(Number) || [];

  let matched = null;

  if (paymentCategory && selectedIds.length) {
    // 결제 카테고리로 로그 재조회
    const guestUserId = route.params?.id || route.query?.id || null;
    if (isCurrent()) await analysisStore.getAnalysisLog(guestUserId, ANALYSIS_TYPE_ID, paymentCategory);
    const cacheKey = `${ANALYSIS_TYPE_ID}_${paymentCategory}`;
    const categoryLogs = analysisLogByType.value[cacheKey] || [];

    matched = categoryLogs.find((log) => {
      const sortedMembers = [...(log.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      const logIds = sortedMembers.map((m) => Number(m.guestUserId));
      if (logIds.length !== selectedIds.length) return false;
      return selectedIds.every((id, i) => id === logIds[i]);
    }) || null;
  }

  // 카테고리 재구매 시 harmonyPaymentData를 새 카테고리로 갱신
  if (pendingCategoryPayment.value) {
    if (isCurrent()) analysisStore.setHarmonyPaymentData({
      category: paymentCategory,
      memberGuestUserIds: selectedIds,
      persons: paymentData?.persons || [],
    });
    pendingCategoryPayment.value = null;
  }

  if (isCurrent()) analysisStore.setHarmonyPurchaseState(true, matched);
  DEV_MODE && console.log('[궁합] 결제 완료 → matchedLog:', matched);

  // 1페이지(첫 번째 콘텐츠)로 이동
  if (isCurrent()) analysisStore.setSelectedContentIndex(null);
  nextTick(() => {
    if (isCurrent()) analysisStore.setSelectedContentIndex(0);
  });

  // 포인트 및 분석권 재조회 (fetchTicketCount 내부에서 궁합 분석권도 자동 파싱)
  await Promise.all([paymentsStore.getOwnPoint(), paymentsStore.fetchTicketCount()]);
};

/** 낭만궁합 시작하기 버튼 클릭 (미구매 상태) */
const onStartHarmony = () => {
  openPaymentPopup();
};

/** 궁합 목록 카드 클릭 → 해당 로그 데이터 store에 세팅 + API 재호출 트리거 */
const pendingLogCard = ref(null);
const dialogLogCardConfirm = ref(null);

const executeLogCardSwitch = (log) => {
  if (!isCurrent()) return;
  if (context) {
    return context.openHistory('harmony', getHarmonyBaseId(log), log.analysisLogId);
  }
  const sortedMembers = [...(log.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  if (isCurrent()) analysisStore.setHarmonyPaymentData({
    category: log.category,
    memberGuestUserIds: sortedMembers.map((m) => Number(m.guestUserId)),
    persons: sortedMembers,
  });
  if (isCurrent()) analysisStore.setHarmonyPurchaseState(true, log);
  if (isCurrent()) analysisStore.setSelectedContentIndex(null);
  nextTick(() => {
    if (isCurrent()) analysisStore.setSelectedContentIndex(0);
  });
};

const onClickLogCard = (log) => {
  if (!isPurchased.value) {
    pendingLogCard.value = log;
    dialogLogCardConfirm.value.openConfirmDialog();
    return;
  }
  executeLogCardSwitch(log);
};

const onConfirmLogCardSwitch = () => {
  if (pendingLogCard.value) {
    executeLogCardSwitch(pendingLogCard.value);
    pendingLogCard.value = null;
  }
};

/** 다른 관계 살펴보기 (구매 완료 상태) → cover로 이동 */
const onExploreOther = () => {
  addContentLogAPI({ contentType: 'ap_harmony_btn_explore_other' });
  if (isCurrent()) analysisStore.resetHarmonyPaymentData();
  if (isCurrent()) analysisStore.requestHarmonyResetSelection();
  if (isCurrent()) analysisStore.clickMainMenu('/overviewNangmanHarmony');
};

/** ===== 다른 관계 살펴보기 — 카테고리별 구매 여부 확인 및 결제 ===== */

/** 구매 완료 시 모든 카테고리 로그 로드 */
const loadAllCategoryLogs = () => {
  const guestUserId = route.params?.id || route.query?.id || null;
  Object.values(CATEGORY_API_MAP).forEach((cat) => {
    const cacheKey = `${ANALYSIS_TYPE_ID}_${cat}`;
    if (!analysisLogByType.value[cacheKey]) {
      analysisStore.getAnalysisLog(guestUserId, ANALYSIS_TYPE_ID, cat);
    }
  });
};

watch(isPurchased, (val) => {
  if (val) loadAllCategoryLogs();
}, { immediate: true });

/** 현재 멤버 조합으로 각 카테고리별 구매 여부 확인 */
const purchasedCategoriesForPair = computed(() => {
  const matched = analysisStore.harmonyMatchedLog;
  if (!matched?.members?.length) return new Set();

  const currentIds = [...matched.members]
    .map((m) => Number(m.guestUserId))
    .sort((a, b) => a - b);

  const purchased = new Set();
  for (const [catId, apiCat] of Object.entries(CATEGORY_API_MAP)) {
    const cacheKey = `${ANALYSIS_TYPE_ID}_${apiCat}`;
    const logs = analysisLogByType.value[cacheKey] || [];
    const has = logs.some((log) => {
      const logIds = [...(log.members || [])].map((m) => Number(m.guestUserId)).sort((a, b) => a - b);
      return logIds.length === currentIds.length && currentIds.every((id, i) => id === logIds[i]);
    });
    if (has) purchased.add(catId);
  }
  return purchased;
});

/** 카테고리 추가 결제 확인 팝업 */
const showCategoryPayConfirm = ref(false);
const pendingCategoryPayment = ref(null);

const onClickExploreCategory = (cat) => {
  if (purchasedCategoriesForPair.value.has(cat.id)) return;
  addContentLogAPI({ contentType: 'ap_harmony_btn_explore_category', option3: cat.label });
  pendingCategoryPayment.value = cat;
  showCategoryPayConfirm.value = true;
};

const onConfirmCategoryPayment = () => {
  // harmonyPaymentData를 변경하지 않음 (CoverPanel watcher가 isPurchased=false로 바꾸는 깜빡임 방지)
  // 결제 API 호출 시 pendingCategoryPayment에서 카테고리만 가져옴
  openPaymentPopup();
};

const onCancelCategoryPayment = () => {
  showCategoryPayConfirm.value = false;
  pendingCategoryPayment.value = null;
};

/** DetailPageHarmony에서 카테고리 결제 트리거 수신 */
watch(
  () => analysisStore.harmonyCategoryPaymentTarget,
  (cat) => {
    if (!cat) return;
    pendingCategoryPayment.value = cat;
    showCategoryPayConfirm.value = true;
    if (isCurrent()) analysisStore.clearHarmonyCategoryPaymentTarget();
  },
);


/** 추가 해석 더보기 항목 — children(월운/세운)에서 동적 가격 */
const extraInterpretations = computed(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  const children = analysisTypeData.value?.children || [];
  const yearlyChild = children.find((c) => c.code === 'COMPAT_YEARLY');
  const monthlyChild = children.find((c) => c.code === 'COMPAT_MONTHLY');

  // 개별 타입 API 응답에서 가격 우선, fallback으로 children의 가격
  const yearlyPrice = extraTypeData.value.yearly?.pointPrice ?? yearlyChild?.pointPrice ?? 300;
  const monthlyPrice = extraTypeData.value.monthly?.pointPrice ?? monthlyChild?.pointPrice ?? 300;

  const yearlyTypeId = yearlyChild?.analysisTypeId ?? 40;
  const monthlyTypeId = monthlyChild?.analysisTypeId ?? 39;

  const items = [
    { id: 'yearly', label: `${year}년`, suffix: ', 우리 사이 분위기', price: yearlyPrice, year, month: null, analysisTypeId: yearlyTypeId },
  ];

  for (let i = 0; i < 3; i++) {
    const raw = month + i;
    const m = raw > 12 ? raw - 12 : raw;
    const y = raw > 12 ? year + 1 : year;
    items.push({
      id: `month${m}`,
      label: `${m}월`,
      suffix: ', 우리 사이 분위기',
      price: monthlyPrice,
      year: y,
      month: m,
      analysisTypeId: monthlyTypeId,
    });
  }

  return items;
});

/** 가격 정보를 store에 공유 (ContentsHarmony03 mood card 가격 표시용) */
watch(extraInterpretations, (items) => {
  const yearly = items.find((i) => i.id === 'yearly');
  const monthly = items.find((i) => i.id.startsWith('month'));
  if (isCurrent()) analysisStore.setHarmonyExtraPrices(yearly?.price ?? 300, monthly?.price ?? 300);
}, { immediate: true });

/** 추가 해석 구매 완료 목록 (parentLogId 기반 children) */
const purchasedExtraIds = ref(new Set());

/** child log에서 extra id를 추출 */
const getExtraId = (child) => {
  if (child.year && child.month) return `month${child.month}`;
  if (child.year) return 'yearly';
  return null;
};

/** child log에서 콘텐츠 데이터를 store에 저장 */
const saveChildContent = (child) => {
  const id = getExtraId(child);
  if (!id) return;
  // child log에 result/data가 있으면 store에 저장
  const content = child.result || child.data || child.content || null;
  if (content) {
    if (isCurrent()) analysisStore.setHarmonyExtraContent(id, content);
  }
};

/** parentLogId 기반 API 호출로 구매 이력 조회 후 열림 상태 반영 */
const syncPurchasedExtras = async () => {
  const matched = analysisStore.harmonyMatchedLog;

  // 이전 데이터 초기화
  if (isCurrent()) analysisStore.resetHarmonyExtras();

  // 1) matchedLog의 childLogs/children에서 먼저 추출
  const childLogs = matched?.childLogs || matched?.children || [];
  const purchased = new Set();

  childLogs.forEach((child) => {
    const id = getExtraId(child);
    if (id) {
      purchased.add(id);
      saveChildContent(child);
    }
  });

  // 2) parentLogId가 있으면 API로 구매 이력 추가 조회
  const userId = matched?.userId || sessionStorage.getItem('authUserId');
  DEV_MODE && console.log('[궁합 추가해석] matched:', JSON.stringify({ analysisLogId: matched?.analysisLogId, parentLogId: matched?.parentLogId, userId }));
  if (matched?.analysisLogId && userId) {
    try {
      const res = await getAnalysisLogAPI({
        userId,
        includeChildTypes: true,
        isValid: true,
        parentLogId: matched.analysisLogId,
        page: 1,
        size: -1,
      });
      if (!isCurrent() || analysisStore.harmonyMatchedLog !== matched) return;
      DEV_MODE && console.log('[궁합 추가해석] 구매 이력 조회 결과:', res?.content);
      (res?.content || []).forEach((child) => {
        const id = getExtraId(child);
        if (id) {
          purchased.add(id);
          saveChildContent(child);
        }
      });
    } catch (e) {
      DEV_MODE && console.error('[궁합 추가해석] 구매 이력 조회 실패:', e);
    }
  }

  purchasedExtraIds.value = purchased;
  // store에 동기화
  if (isCurrent()) analysisStore.setHarmonyPurchasedExtraIds(purchased);

  // 구매된 세운/월운 데이터 API 호출 (1:1일 때만)
  const memberCount = matched?.members?.length || 0;
  if (purchased.size > 0 && matched && memberCount === 2) {
    const sortedMembers = [...(matched.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const guestUserId = sortedMembers[0]?.guestUserId;
    const now = new Date();

    for (const id of purchased) {
      if (analysisStore.harmonyExtraContents[id]) continue;
      const isYearly = id === 'yearly';
      const params = {
        guestUserId,
        category: matched.category,
        parentLogId: matched.analysisLogId,
        analysisTypeId: isYearly ? 40 : 39,
        year: now.getFullYear(),
      };
      if (!isYearly) params.month = parseInt(id.replace('month', ''));
      try {
        const result = await fetchHarmonyServiceAPI(params);
        if (!isCurrent() || analysisStore.harmonyMatchedLog !== matched) return;
        DEV_MODE && console.log(`[궁합 PC] ${id} API 응답:`, result?.data);
        if (isCurrent()) analysisStore.setHarmonyExtraContent(id, result?.data);
      } catch (e) {
        DEV_MODE && console.error(`[궁합 PC] ${id} API 호출 실패:`, e);
      }
    }
  }
};

watch(() => analysisStore.harmonyMatchedLog, syncPurchasedExtras, { immediate: true, deep: true });

/** 추가해석 항목이 구매 완료인지 확인 */
const isExtraPurchased = (itemId) => purchasedExtraIds.value.has(itemId);

/** 추가해석 결제 팝업 */
const showExtraPaymentPopup = ref(false);
const extraBuyTarget = ref(null);

/** 월 선택 팝업 */
const showMonthSelectPopup = ref(false);
const selectedMonthId = ref(null);

/** 월 선택 팝업에서 표시할 월별 항목 (구매완료 포함, 비활성화 표시) */
const monthSelectItems = computed(() => {
  return extraInterpretations.value.filter((item) => item.id.startsWith('month'));
});

/** 미구매 월별 항목만 */
const unpurchasedMonthItems = computed(() => {
  return monthSelectItems.value.filter((item) => !isExtraPurchased(item.id));
});

/** 선택된 항목이 구매 가능한지 */
const canConfirmMonth = computed(() => {
  return selectedMonthId.value && !isExtraPurchased(selectedMonthId.value);
});

const openMonthSelectPopup = async () => {
  // 만 99세 초과 멤버 확인
  const overAgeMembers = await getOverAgeLimitMembers();
  if (overAgeMembers.length > 0) {
    const names = overAgeMembers.map((m) => m.guestUserName || m.name || '').filter(Boolean).join(', ');
    dialogTitle.value = '구매할 수 없습니다';
    dialogText.value = names
      ? `${names}님은 만 ${MAX_AGE_LIMIT}세를 초과하여\n추가 해석을 구매할 수 없습니다.`
      : `만 ${MAX_AGE_LIMIT}세를 초과한 대상자가 포함되어\n추가 해석을 구매할 수 없습니다.`;
    dialogNextUrl.value = null;
    dialogAlert.value.openAlertDialog();
    return;
  }

  const unpurchased = unpurchasedMonthItems.value;
  // 미구매 항목이 있으면 첫번째를 기본 선택, 없으면 선택 없이 열기
  selectedMonthId.value = unpurchased.length > 0 ? unpurchased[0].id : null;
  showMonthSelectPopup.value = true;
  paymentsStore.getOwnPoint();
};

const closeMonthSelectPopup = () => {
  showMonthSelectPopup.value = false;
  selectedMonthId.value = null;
};

const onConfirmMonthSelect = async () => {
  if (!canConfirmMonth.value) return;
  const item = extraInterpretations.value.find((i) => i.id === selectedMonthId.value);
  if (!item) return;

  // 만 99세 초과 멤버 확인 (구독/결제 공통 — 분석 불가)
  const overAgeMembers = await getOverAgeLimitMembers();
  if (overAgeMembers.length > 0) {
    const names = overAgeMembers.map((m) => m.guestUserName || m.name || '').filter(Boolean).join(', ');
    dialogTitle.value = '추가 해석을 볼 수 없습니다';
    dialogText.value = names
      ? `${names}님은 만 ${MAX_AGE_LIMIT}세를 초과하여\n추가 해석을 볼 수 없습니다.`
      : `만 ${MAX_AGE_LIMIT}세를 초과한 대상자가 포함되어\n추가 해석을 볼 수 없습니다.`;
    dialogNextUrl.value = null;
    dialogAlert.value.openAlertDialog();
    return;
  }

  // 구독이 이 조합을 커버하면 결제 없이 조회
  if (subscriptionCovers.value) {
    showMonthSelectPopup.value = false;
    await viewExtraBySubscription(item);
    return;
  }

  // 포인트 부족 체크
  const userPoints = paymentsStore?.points ?? 0;
  if (userPoints < item.price) {
    showMonthSelectPopup.value = false;
    extraBuyTarget.value = item;
    goExtraChargePoint();
    return;
  }

  // 확인 팝업 없이 바로 결제
  showMonthSelectPopup.value = false;
  extraBuyTarget.value = item;
  await onConfirmExtraPayment();
};

/** 멤버 중 만 나이 99세 초과자가 있는지 확인 */
const MAX_AGE_LIMIT = 99;

const calcRealAge = (solarStr) => {
  if (!solarStr) return null;
  const [yearStr, monthStr, dayStr] = solarStr.split('-');
  const birthYear = Number(yearStr);
  const birthMonth = Number(monthStr);
  const birthDay = Number(dayStr);
  if (!birthYear) return null;

  const now = new Date();
  let age = now.getFullYear() - birthYear;
  const todayMonth = now.getMonth() + 1;
  const todayDay = now.getDate();
  if (todayMonth < birthMonth || (todayMonth === birthMonth && todayDay < birthDay)) {
    age -= 1;
  }
  return age;
};

const getOverAgeLimitMembers = async () => {
  const persons = harmonyPaymentData.value?.persons || analysisStore.harmonyMatchedLog?.members || [];
  if (!persons.length) return [];

  // solar 필드가 있는 경우 바로 체크, 없으면 API로 조회
  const needsFetch = persons.some((m) => !m.solar);

  let enrichedPersons = persons;
  if (needsFetch) {
    try {
      const details = await Promise.all(
        persons.map((m) => getGuestUserDetail(m.guestUserId).catch(() => null)),
      );
      enrichedPersons = persons.map((m, i) => ({
        ...m,
        solar: m.solar || details[i]?.solar || '',
      }));
    } catch {
      DEV_MODE && console.error('[궁합 나이제한] 사주 정보 조회 실패');
      return [];
    }
  }

  return enrichedPersons.filter((m) => {
    const age = calcRealAge(m.solar);
    return age !== null && age > MAX_AGE_LIMIT;
  });
};

/** 구독자: 결제 없이 member_ids로 추가해석 조회 (구독 커버 시). parentLogId 불필요 */
const viewExtraBySubscription = async (item) => {
  const persons = harmonyPaymentData.value?.persons || [];
  const sorted = [...persons].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const fetchParams = {
    guestUserId: Number(sorted[0]?.guestUserId),
    memberIds: sorted.map((p) => Number(p.guestUserId)),
    category: harmonyPaymentData.value?.category,
    analysisTypeId: item.analysisTypeId,
    year: item.year,
    suggestionFlag: 3,
  };
  if (item.month) fetchParams.month = item.month;
  try {
    const res = await fetchHarmonyServiceAPI(fetchParams);
    if (!isCurrent()) return;
    if (res?.data) {
      if (isCurrent()) analysisStore.setHarmonyExtraContent(item.id, res.data);
      purchasedExtraIds.value.add(item.id);
      if (isCurrent()) analysisStore.setHarmonyPurchasedExtraIds(purchasedExtraIds.value);
    }
  } catch (e) {
    DEV_MODE &&
      console.error('[궁합 추가해석 구독조회] 실패', e?.response?.status, e?.response?.data);
  }
};

const onClickExtraItem = async (item) => {
  if (isExtraPurchased(item.id)) return;

  // 만 99세 초과 멤버 확인 (구독/결제 공통 — 분석 불가)
  const overAgeMembers = await getOverAgeLimitMembers();
  if (overAgeMembers.length > 0) {
    const names = overAgeMembers.map((m) => m.guestUserName || m.name || '').filter(Boolean).join(', ');
    dialogTitle.value = '추가 해석을 볼 수 없습니다';
    dialogText.value = names
      ? `${names}님은 만 ${MAX_AGE_LIMIT}세를 초과하여\n추가 해석을 볼 수 없습니다.`
      : `만 ${MAX_AGE_LIMIT}세를 초과한 대상자가 포함되어\n추가 해석을 볼 수 없습니다.`;
    dialogNextUrl.value = null;
    dialogAlert.value.openAlertDialog();
    return;
  }

  // 구독이 이 조합을 커버하면 결제 없이 조회
  if (subscriptionCovers.value) {
    await viewExtraBySubscription(item);
    return;
  }

  extraBuyTarget.value = item;
  showExtraPaymentPopup.value = true;
  paymentsStore.getOwnPoint();
};

/** 구독자: 월별 추가해석을 월 선택 없이 전부(현재월~3개월) 조회 */
const viewAllMonthsBySubscription = async () => {
  const monthlyItems = extraInterpretations.value.filter(
    (i) => i.id.startsWith('month') && !isExtraPurchased(i.id),
  );
  await Promise.all(monthlyItems.map((item) => viewExtraBySubscription(item)));
};

/** 구독자: 조합 데이터 준비되면 추가해석(올해+월별)을 클릭 없이 병렬 자동 조회 */
const autoLoadExtrasForSubscription = async () => {
  // 만 99세 초과 멤버가 있으면 추가해석 분석 불가 → 자동조회 스킵
  const overAge = await getOverAgeLimitMembers();
  if (overAge.length > 0) return;
  if (isCurrent()) analysisStore.setHarmonyExtrasAutoLoading(true);
  try {
    const yearly = extraInterpretations.value.find((i) => i.id === 'yearly');
    const tasks = [viewAllMonthsBySubscription()];
    if (yearly && !isExtraPurchased(yearly.id)) tasks.push(viewExtraBySubscription(yearly));
    await Promise.all(tasks);
  } finally {
    if (isCurrent()) analysisStore.setHarmonyExtrasAutoLoading(false);
  }
};
watch(
  () => harmonyPaymentData.value,
  () => {
    if (!subscriptionCovers.value) return;
    // 카테고리별 갱신을 위해 이전 추가해석 상태 초기화 후 재조회
    purchasedExtraIds.value = new Set();
    if (isCurrent()) analysisStore.resetHarmonyExtras();
    autoLoadExtrasForSubscription();
  },
  { immediate: true },
);

/** ContentsHarmony03 mood card에서 트리거한 추가해석 결제 요청 감시 */
watch(
  () => analysisStore.harmonyExtraPaymentTarget,
  (targetId) => {
    if (!targetId) return;
    if (targetId === 'monthly-select') {
      // 구독자는 월 선택 없이 전부 조회 (모두 무료 열람)
      if (subscriptionCovers.value) {
        viewAllMonthsBySubscription();
      } else {
        openMonthSelectPopup();
      }
      if (isCurrent()) analysisStore.clearHarmonyExtraPaymentTarget();
      return;
    }
    const item = extraInterpretations.value.find((i) => i.id === targetId);
    if (item) {
      onClickExtraItem(item);
    }
    if (isCurrent()) analysisStore.clearHarmonyExtraPaymentTarget();
  },
);

const closeExtraPaymentPopup = () => {
  showExtraPaymentPopup.value = false;
  extraBuyTarget.value = null;
};

/** 추가해석 포인트 부족 → 충전 팝업 표시 */
const goExtraChargePoint = () => {
  const price = extraBuyTarget.value?.price || 0;
  showExtraPaymentPopup.value = false;
  chargePopupAnalysisType.value = {
    pointPrice: price,
    pointBasePrice: price,
  };
  showChargePopup.value = true;
};

/** 추가해석 결제 API 호출 */
const isExtraPaymentProcessing = ref(false);

const onConfirmExtraPayment = async () => {
  if (!isCurrent() || isExtraPaymentProcessing.value) return;
  isExtraPaymentProcessing.value = true;
  showExtraPaymentPopup.value = false;

  try {
    const matched = analysisStore.harmonyMatchedLog;
    if (!matched) throw new Error('매칭된 궁합 로그가 없습니다.');
    if (context && getHarmonyBaseId(matched) !== Number(context.saju.value?.guestUserId)) return;

    const item = extraBuyTarget.value;
    if (!item) throw new Error('결제 대상이 없습니다.');

    // guestUserId: route params 우선 → members 중 sortOrder 0번 → fallback
    const sortedMembers = [...(matched.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const primaryGuestUserId = Number(route.params?.id) || sortedMembers[0]?.guestUserId || harmonyPaymentData.value?.memberGuestUserIds?.[0];

    const params = {
      guestUserId: primaryGuestUserId,
      analysisTypeId: item.analysisTypeId,
      paymentMethod: 'point',
      category: matched.category || harmonyPaymentData.value?.category,
      parentLogId: matched.analysisLogId,
      year: item.year,
    };

    if (item.month) {
      params.month = item.month;
    }

    DEV_MODE && console.log('[궁합 추가해석] 결제 요청 params:', params);

    const postRes = await postAnalysisLogAPI(params);
    if (!isCurrent()) return;

    // 구매 완료 → UI 즉시 반영
    purchasedExtraIds.value.add(item.id);
    if (isCurrent()) analysisStore.setHarmonyPurchasedExtraIds(purchasedExtraIds.value);

    // 응답에 콘텐츠가 포함되어 있으면 store에 저장
    const resContent = postRes?.result || postRes?.data || postRes?.content || null;
    if (resContent) {
      if (isCurrent()) analysisStore.setHarmonyExtraContent(item.id, resContent);
    }

    // 포인트 및 분석권 재조회
    paymentsStore.getOwnPoint();
    paymentsStore.fetchTicketCount();

    // 종합 조언 페이지(content3)로 이동
    if (isCurrent()) analysisStore.setSelectedContentIndex(2);

    dialogTitle.value = '성공적으로 결제되었습니다.';
    dialogText.value = '';
    dialogNextUrl.value = null;
    dialogAlert.value.openAlertDialog();

    // 결제 API 응답에 콘텐츠가 없으면 백그라운드에서 별도 조회 (알림 차단 방지)
    if (!resContent) {
      const sortedMembersForFetch = [...(matched.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      const fetchParams = {
        guestUserId: sortedMembersForFetch[0]?.guestUserId || primaryGuestUserId,
        category: matched.category || harmonyPaymentData.value?.category,
        parentLogId: matched.analysisLogId,
        analysisTypeId: item.analysisTypeId,
        year: item.year,
      };
      if (item.month) fetchParams.month = item.month;

      DEV_MODE && console.log('[궁합 추가해석] 콘텐츠 별도 조회 params:', fetchParams);
      fetchHarmonyServiceAPI(fetchParams).then((fetchRes) => {
        if (!isCurrent() || analysisStore.harmonyMatchedLog !== matched) return;
        DEV_MODE && console.log(`[궁합 추가해석] ${item.id} 콘텐츠 조회 결과:`, fetchRes?.data);
        if (fetchRes?.data) {
          if (isCurrent()) analysisStore.setHarmonyExtraContent(item.id, fetchRes.data);
        }
      }).catch((fetchErr) => {
        DEV_MODE && console.error(`[궁합 추가해석] ${item.id} 콘텐츠 조회 실패:`, fetchErr);
      });
    }
  } catch (e) {
    DEV_MODE && console.error('[궁합 추가해석] 결제 실패:', e);
    dialogTitle.value = e?.response?.data?.detail || e?.response?.data?.message || '결제에 실패했습니다.';
    dialogText.value = '';
    dialogNextUrl.value = null;
    dialogAlert.value.openAlertDialog();
  } finally {
    isExtraPaymentProcessing.value = false;
    extraBuyTarget.value = null;
  }
};
</script>

<template>
  <div class="detail-panel-harmony">
    <div class="detail-panel-harmony__content">
      <!-- ===== 구매/구독 열람 상태 ===== -->
      <template v-if="canViewFull">
        <!-- 추가 해석 더보기 (1대1 = 2명일 때만 표시) -->
        <div v-if="false" class="extra-interpret-box">
          <div class="extra-interpret-box__title">추가 해석 더보기</div>
          <div class="extra-interpret-box__list">
            <template v-for="(item, idx) in extraInterpretations" :key="item.id">
              <div class="extra-interpret-item">
                <div class="extra-interpret-item__label">
                  <span class="extra-interpret-item__highlight">{{ item.label }}</span>{{ item.suffix }}
                </div>
                <div
                  v-if="isExtraPurchased(item.id)"
                  class="extra-interpret-item__opened"
                >
                  열림
                </div>
                <div
                  v-else
                  class="extra-interpret-item__price"
                  @click="onClickExtraItem(item)"
                >
                  {{ item.price }} P
                </div>
              </div>
              <div v-if="idx < extraInterpretations.length - 1" class="extra-interpret-divider"></div>
            </template>
          </div>
        </div>
      </template>

      <!-- ===== 미구매 상태 ===== -->
      <template v-else>
        <!-- 선택 정보 요약 -->
        <div v-if="harmonyPaymentData" class="harmony-selection-summary">
          <div class="harmony-selection-summary__row">
            <span class="harmony-selection-summary__label">대상</span>
            <span class="harmony-selection-summary__value">{{ memberSummary }}</span>
          </div>
          <div class="harmony-selection-summary__row">
            <span class="harmony-selection-summary__label">인원</span>
            <span class="harmony-selection-summary__value">{{ harmonyPaymentData.memberGuestUserIds?.length || 0 }}명</span>
          </div>
        </div>

        <!-- 보유 궁합분석권 -->
        <div class="harmony-ticket-box">
          <div class="harmony-ticket-box__left">
            <i class="ticket-icon__analysis"></i>
            <span class="harmony-ticket-box__label">보유 궁합분석권</span>
          </div>
          <span class="harmony-ticket-box__count">{{ matchedTicketDisplayText }}</span>
        </div>
      </template>

      <!-- ===== 구매한 내역 (탭 방식 — CoverPanel과 동일) ===== -->
      <div class="harmony-list-section">
        <div class="harmony-list-title">구매한 내역</div>
        <div
          ref="categoryTabsRef"
          class="harmony-category-tabs"
          @mousedown="onTabsMouseDown"
          @touchstart="onTabsTouchStart"
        >
          <button
            v-for="cat in HARMONY_CATEGORIES"
            :key="cat.id"
            class="harmony-category-tab"
            :class="{
              'harmony-category-tab--active': selectedCategory === cat.id,
              'harmony-category-tab--disabled': !categoryPurchaseMap[cat.id],
            }"
            :disabled="!categoryPurchaseMap[cat.id]"
            @click="onChangeCategory(cat.id)"
          >
            <span class="harmony-category-tab__emoji">{{ cat.emoji }}</span>
            <span class="harmony-category-tab__label">{{ cat.label }}</span>
          </button>
        </div>
        <div class="harmony-list-content" v-if="!isCategoryLoading">
          <template v-if="!hasAnyPurchase || !filteredLogData.length">
            <div class="harmony-list-card">
              <div class="harmony-list-empty">아직 구매한 궁합 서비스가 없습니다</div>
            </div>
          </template>
          <template v-else>
            <div
              v-for="item in filteredLogData"
              :key="item.analysisLogId"
              class="harmony-list-card"
              :class="{ 'harmony-list-card--active': analysisStore.harmonyMatchedLog?.analysisLogId === item.analysisLogId }"
              @click="onClickLogCard(item)"
            >
              <div class="harmony-list-item">
                <div class="harmony-list-item__info">
                  <div class="harmony-list-item__name">
                    {{ [...(item.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).map((m) => { const n = m.guestUserName || '-'; return n.length > 4 ? n.slice(0, 4) + '…' : n; }).join(' & ') || '궁합 분석' }}
                  </div>
                  <div class="harmony-list-item__dates">
                    <span class="harmony-list-item__date">{{ formatListDate(item.createdAt) }}</span>
                    <span class="harmony-list-item__expiry" v-if="item.expiryDate">
                      유효기간 {{ formatListDate(item.expiryDate) }}
                    </span>
                    <span class="harmony-list-item__expiry" v-else>소장용</span>
                  </div>
                </div>
                <i class="harmony-list-item__arrow"></i>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 하단 -->
    <div class="detail-panel-harmony__footer">
      <!-- 미열람: 결제정보 + 시작 버튼 -->
      <template v-if="!canViewFull">
        <div class="harmony-payment-info">
          <div class="harmony-payment-info__header">
            <span class="harmony-payment-info__title">총 결제할 포인트</span>
            <span class="harmony-payment-info__price">{{ pointPrice.toLocaleString() }} P</span>
          </div>
          <div class="payment-grid">
            <span class="label">보유 포인트</span>
            <span class="value">{{ paymentsStore?.formattedPoints || 0 }} P</span>
            <div class="divider-col"><span class="bar"></span></div>
            <span class="label">보유 분석권</span>
            <span class="value">{{ matchedTicketInfo.count }}건</span>
          </div>
          <div class="shortage-row">
            <span class="label">부족한 포인트</span>
            <span class="value">{{ shortagePoints.toLocaleString() }} P</span>
          </div>
        </div>
        <button class="harmony-start-btn" @click="onStartHarmony">
          낭만궁합 시작하기
        </button>
      </template>

      <!-- 구매완료: 새 궁합보기는 LNB 버튼으로 이동 -->
      <template v-else></template>
    </div>
  </div>

  <!-- 결제 팝업 -->
  <Teleport to="body">
    <!-- 포인트 충전 팝업 -->
    <PopAnalysisInfo
      :isVisible="showChargePopup"
      :analysisType="chargePopupAnalysisType"
      @close="handleCloseChargePopup"
      @charge="handleGoCharge"
    />
    <div v-if="showPaymentPopup || showCategoryPayConfirm" class="pop-analysis-info-backdrop">
      <!-- 카테고리 추가 결제 확인 (결제 팝업 열리기 전 단계) -->
      <div v-if="showCategoryPayConfirm && !showPaymentPopup" class="extra-pay-popup">
        <div class="extra-pay-popup__text">
          <p class="extra-pay-popup__title">{{ pendingCategoryPayment?.emoji }} {{ pendingCategoryPayment?.label }} 궁합</p>
          <p class="extra-pay-popup__desc">구매 후 열람하시겠습니까?</p>
          <p class="extra-pay-popup__price">결제 포인트 {{ pointPrice.toLocaleString() }} P</p>
        </div>
        <div class="extra-pay-popup__buttons">
          <button class="extra-pay-popup__btn extra-pay-popup__btn--cancel" @click="onCancelCategoryPayment">취소</button>
          <button class="extra-pay-popup__btn extra-pay-popup__btn--confirm" @click="onConfirmCategoryPayment">구매</button>
        </div>
      </div>
      <!-- 결제 방법 선택 -->
      <div v-if="showPaymentPopup" class="pop-analysis-info">
        <div class="pop-header">
          <div class="pop-title">사용하실 결제 방법을 선택해주세요!</div>
          <button class="pop-close" @click="closePaymentPopup"></button>
        </div>
        <div class="pop-info-row">
          <span class="pop-info-label">보유중</span>
          <div class="pop-info-point-row">
            <span class="pop-info-point">{{ paymentsStore?.formattedPoints }} P</span>
            <span class="pop-info-divider">|</span>
            <div class="pop-info-ticket">
              <span>궁합분석권</span>
              <span>{{ matchedTicketDisplayText }}</span>
            </div>
          </div>
        </div>
        <div class="pop-options-row">
          <div
            class="option-box point"
            :class="{ active: buyType === 'point', disabled: !isPointActive }"
            @click="handleBuyPoint"
          >
            <div v-if="discountText" class="option-discount-badge">{{ discountText }}</div>
            <div class="option-icon-n"></div>
            <div class="option-desc-col">
              <div class="option-title">포인트 사용하기</div>
              <div class="option-price-row">
                <template v-if="pointBasePrice && pointBasePrice > pointPrice">
                  <span class="option-base-price">{{ pointBasePrice.toLocaleString() }} P</span>
                  <span class="option-price-arrow"></span>
                </template>
                <span class="option-value">{{ pointPrice.toLocaleString() }} P</span>
              </div>
            </div>
          </div>
          <div class="option-box ticket-upsell" @click="goTicketProduct">
            <span
              v-if="ticketProduct?.perUnitPrice != null && ticketProduct.discountPct > 0"
              class="option-discount-badge"
              >{{ ticketProduct.discountPct }}% 할인</span
            >
            <div class="option-icon-ticket"></div>
            <div class="option-desc-col">
              <div class="option-title">할인 분석권</div>
              <div v-if="ticketProduct?.perUnitPrice != null" class="option-value">
                1{{ ticketProduct.unit }}당 {{ formatNumber(ticketProduct.perUnitPrice) }} P
              </div>
              <div v-else class="option-value upsell-fallback">상품 보기</div>
            </div>
            <span class="upsell-arrow" aria-hidden="true">›</span>
          </div>
        </div>
        <button
          class="paywall_use_button_confirm"
          :class="{ disabled: !buyType }"
          @click="goConfirm"
        >
          확인
        </button>
      </div>
      <!-- backdrop 내부에 둬야 같은 스태킹 컨텍스트에서 결제 팝업 위로 표시됨 -->
      <ConfirmComponent
        ref="ticketConfirmRef"
        title="할인 분석권 구매 화면으로 이동하면 현재 작성 중인 내용이 초기화될 수 있습니다."
        title2="이동하시겠습니까?"
        title2-class="confirm-highlight-orange"
        text="네"
        cancel-text="아니오"
        :next-url="moveToTicketProduct"
      />
    </div>

    <!-- 추가해석 결제 팝업 -->
    <div v-if="showExtraPaymentPopup && extraBuyTarget" class="pop-analysis-info-backdrop">
      <div class="extra-pay-popup">
        <div class="extra-pay-popup__text">
          <p class="extra-pay-popup__title">{{ extraBuyTarget.label }}{{ extraBuyTarget.suffix }}</p>
          <p class="extra-pay-popup__desc">구매 후 열람하시겠습니까?</p>
          <p class="extra-pay-popup__price">결제 포인트 {{ extraBuyTarget.price.toLocaleString() }} P</p>
        </div>
        <div class="extra-pay-popup__buttons">
          <button class="extra-pay-popup__btn extra-pay-popup__btn--cancel" @click="closeExtraPaymentPopup">취소</button>
          <button
            v-if="currentPoints >= extraBuyTarget.price"
            class="extra-pay-popup__btn extra-pay-popup__btn--confirm"
            @click="onConfirmExtraPayment()"
          >
            구매
          </button>
          <button
            v-else
            class="extra-pay-popup__btn extra-pay-popup__btn--charge"
            @click="goExtraChargePoint()"
          >
            포인트 충전하기
          </button>
        </div>
      </div>
    </div>

    <!-- 월 선택 팝업 -->
    <div v-if="showMonthSelectPopup" class="pop-analysis-info-backdrop">
      <div class="month-select-popup">
        <div class="month-select-popup__header">
          <p class="month-select-popup__title">보고 싶은 달을 선택해주세요.</p>
          <button class="month-select-popup__close" @click="closeMonthSelectPopup"></button>
        </div>
        <div class="month-select-popup__body">
          <div v-if="!subscriptionCovers" class="month-select-popup__info">
            <span class="month-select-popup__info-label">보유중</span>
            <span class="month-select-popup__info-point">{{ paymentsStore?.formattedPoints }} P</span>
          </div>
          <div class="month-select-popup__options">
            <div
              v-for="item in monthSelectItems"
              :key="item.id"
              class="month-select-popup__option"
              :class="{
                active: selectedMonthId === item.id && !isExtraPurchased(item.id),
                disabled: isExtraPurchased(item.id),
              }"
              @click="!isExtraPurchased(item.id) && (selectedMonthId = item.id)"
            >
              <p class="month-select-popup__option-label">{{ item.year }}년 {{ item.month }}월</p>
              <p v-if="isExtraPurchased(item.id)" class="month-select-popup__option-purchased">{{ subscriptionCovers ? '열람완료' : '구매완료' }}</p>
              <p v-else-if="!subscriptionCovers" class="month-select-popup__option-price">
                {{ item.price }} P
              </p>
            </div>
          </div>
        </div>
        <button
          class="month-select-popup__confirm"
          :class="{ 'month-select-popup__confirm--disabled': !canConfirmMonth }"
          :disabled="!canConfirmMonth"
          @click="onConfirmMonthSelect"
        >{{ subscriptionCovers ? '보기' : '결제하기' }}</button>
      </div>
    </div>

    <!-- Confirm Dialog -->
    <ConfirmComponentPay
      ref="dialogConfirm"
      :title="dialogTitle"
      :title2="dialogTitle2"
      :text="dialogText"
      :nextUrl="dialogNextUrl"
      :warning="warningTxt"
      :doUrl="dialogDoUrl"
      logContentType="ap_harmony_payment_final_opt3"
      logCancelOption3="취소"
      logConfirmOption3="사용"
    />

    <!-- Alert Dialog -->
    <AlertComponent
      ref="dialogAlert"
      :title="dialogTitle"
      :text="dialogText"
      :nextUrl="dialogNextUrl"
    />

    <!-- 미구매 상태에서 카드 전환 확인 Dialog -->
    <ConfirmComponentPay
      ref="dialogLogCardConfirm"
      title="구매한 궁합 결과로"
      title2="이동하시겠습니까?"
      text="이동"
      :doUrl="onConfirmLogCardSwitch"
    />
  </Teleport>
</template>

<style lang="scss" scoped>
@import 'src/assets/nangman_style/_analysis_main_color.scss';

.detail-panel-harmony {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

.detail-panel-harmony__content {
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 28px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

.detail-panel-harmony__footer {
  display: flex;
  flex-direction: column;
  gap: 28px;
  align-items: center;
  padding: 28px;
  width: 100%;
  box-sizing: border-box;
  background: var(--gray-100-white, #fff);
}

/* 추가 해석 더보기 */
.extra-interpret-box {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  box-sizing: border-box;

  &__title {
    color: var(--Gray-800, #333);
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: center;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }
}

.extra-interpret-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 5px 15px;
  box-sizing: border-box;
  border-radius: 8px;

  &__label {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
  }

  &__highlight {
    color: #ff6b6b;
  }

  &__price {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 100px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    color: var(--Gray-600, #757575);
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--orange-300-main, #eb4c10);
      color: var(--orange-300-main, #eb4c10);
      background: var(--Orange-100, #fff8f5);
    }
  }

  &__opened {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 65px;
    padding: 6px 12px;
    border-radius: 100px;
    background: var(--Orange-400, #ff6b6b);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;
  }
}

.extra-interpret-divider {
  width: calc(100% - 72px);
  height: 1px;
  background: var(--Gray-300, #ebebeb);
}

/* 선택 정보 요약 */
.harmony-selection-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  box-sizing: border-box;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__label {
    color: var(--Gray-600, #757575);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  &__value {
    color: var(--Gray-800, #333);
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
  }
}

/* 보유 궁합분석권 */
.harmony-ticket-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  box-sizing: border-box;

  &__left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__label {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__count {
    color: var(--orange-300-main, #eb4c10);
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
  }
}

.ticket-icon__analysis {
  background: url('@manseImages/ticket-icon.svg') center center no-repeat;
  background-size: contain;
  width: 20px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
}

/* 다른 관계 살펴보기 */
.explore-other-section {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }
}

.explore-other-categories {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
}

.explore-category-btn {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  border-radius: 8px;
  background: var(--gray-100-white, #fff);
  border: 1px solid var(--Gray-300, #ebebeb);
  cursor: pointer;
  transition: all 0.15s ease;

  &__emoji {
    font-size: 18px;
    line-height: 25px;
    flex-shrink: 0;
  }

  &__label {
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    color: var(--Gray-600, #757575);
    white-space: nowrap;
  }

  &:hover:not(.explore-category-btn--disabled) {
    border-color: var(--orange-300-main, #eb4c10);

    .explore-category-btn__label {
      color: var(--orange-300-main, #eb4c10);
    }
  }

  &--disabled {
    background: var(--Gray-200, #f8f8f8);
    border-color: transparent;
    cursor: default;
    opacity: 1;
  }
}


/* 궁합목록 섹션 */
.harmony-list-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.harmony-list-title {
  color: var(--Gray-800, #333);
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.harmony-category-tabs {
  display: flex;
  gap: 10px;
  align-items: center;
  white-space: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  user-select: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &:active {
    cursor: grabbing;
  }
}

.harmony-category-tab {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: center;
  padding: 5px 15px;
  border-radius: 100px;
  background: var(--Gray-200, #f8f8f8);
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  &__emoji {
    font-size: 18px;
    line-height: 25px;
  }

  &__label {
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    color: var(--Gray-600, #757575);
  }

  &--active {
    background: var(--Orange-100, #fff8f5);
    border-color: var(--Orange-200, #ff8800);

    .harmony-category-tab__label {
      color: var(--orange-300-main, #eb4c10);
      font-weight: 700;
    }
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: var(--Gray-200, #f8f8f8);
    }
  }
}

.harmony-list-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.harmony-list-card {
  border: 1px solid var(--Gray-300, #ebebeb);
  border-radius: 10px;
  overflow: hidden;
  background: var(--gray-100-white, #fff);
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  transition: border-color 0.2s ease;

  &--active {
    border-color: var(--Orange-200, #ff8800);
    background: var(--Orange-100, #fff8f5);
  }
}

.harmony-list-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  padding: 16px 20px;
  color: var(--Gray-800, #333);
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  box-sizing: border-box;
}

.harmony-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  cursor: pointer;

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }

  &__name {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__dates {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    font-size: 13px;
    line-height: 18px;
    width: 100%;
  }

  &__date {
    color: var(--Gray-800, #333);
    font-weight: 400;
  }

  &__expiry {
    color: var(--orange-300-main, #eb4c10);
    font-weight: 500;
  }

  &__arrow {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    background: url('@manseImages/icon_info_detail.png') center center no-repeat;
    background-size: 20px auto;
  }
}

/* 결제 정보 박스 */
.harmony-payment-info {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 19px 25px;
  border-radius: 12px;
  background: var(--Orange-100, #fff8f5);
  width: 100%;
  box-sizing: border-box;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
  }

  &__price {
    color: var(--orange-300-main, #eb4c10);
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
    text-align: right;
  }

}

.payment-grid,
.shortage-row {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .label {
    font-size: 14px;
    color: #333;
  }

  .value {
    color: var(--orange-300-main, #eb4c10);
    text-align: right;
    font-size: 13px;
    font-weight: 700;
    line-height: 130%;
  }
}


.divider-col {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;

  .bar {
    width: 1px;
    height: 10px;
    background-color: #e0e0e0;
  }
}

/* 시작 버튼 */
.harmony-start-btn {
  display: flex;
  width: 100%;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  border: none;
  background: var(--orange-300-main, #eb4c10);
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #d4440e;
  }
}

/* ===== 결제 팝업 스타일 (ChoicePaymentPopup 참조) ===== */
.pop-analysis-info-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pop-analysis-info {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  width: 400px;
  padding: 30px 32px 32px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pop-header {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 18px;

  .pop-close {
    position: absolute;
    right: 16px;
    top: 30px;
  }
}

.pop-title {
  color: var(--Gray-800, #333);
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.pop-close {
  background: url('@manseImages/icon_close_1.svg');
  background-size: 24px 24px;
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.pop-info-row {
  display: flex;
  padding: 12px 20px;
  align-items: center;
  align-self: stretch;
  background: var(--Gray-200, #f8f8f8);
  color: var(--Gray-700, #575757);
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  margin-bottom: 12px;
  position: relative;
}

.pop-info-label {
  color: var(--orange-300-main, #eb4c10);
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.pop-info-point-row {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
}

.pop-info-point {
  flex: 1;
  text-align: right;
}

.pop-info-divider {
  color: #d9d9d9;
  margin: 0 12px;
}

.pop-info-ticket {
  flex: 1;
  display: flex;
  justify-content: space-between;
}

.pop-options-row {
  width: 100%;
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
}

.option-box {
  position: relative;
  flex: 1;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0 16px;
  color: var(--Gray-800, #333);
  gap: 12px;
  cursor: pointer;
  box-sizing: border-box;

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.active {
    border: 2px solid var(--orange-300-main, #eb4c10) !important;
    background: var(--Orange-100, #fff8f5);
    margin: -1px;
    color: var(--orange-300-main, #eb4c10);
  }
}

.option-icon-n {
  width: 40px;
  height: 40px;
  background: url('@manseImages/icon_an_guide_point.png') no-repeat center/cover;
  margin-bottom: 4px;
}

.option-icon-ticket {
  width: 40px;
  height: 40px;
  background: url('@manseImages/icon_an_guide_Frame.png') no-repeat center/cover;
  margin-bottom: 4px;
}

.option-desc-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.option-title {
  color: var(--Gray-800, #333);
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
}

.option-value {
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.option-price-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
}

.option-base-price {
  color: var(--Gray-600, #757575);
  text-align: center;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 700;
  line-height: 24px;
  text-decoration-line: line-through;
}

.option-price-arrow {
  width: 20px;
  height: 20px;
  background: url('@manseImages/icon_arrow_right.svg') center center no-repeat;
  background-size: 20px 20px;
}

.option-discount-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  height: 18px;
  padding: 3px 6px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #eb4c10;
  color: #fff;
  text-align: center;
  font-family: Pretendard;
  font-size: 11px;
  font-weight: 700;
  line-height: 12px;
}

.upsell-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--Gray-500, #949494);
  font-size: 22px;
  font-weight: 400;
  line-height: 1;
}

.upsell-fallback {
  color: var(--orange-300-main, #eb4c10);
}

.paywall_use_button_confirm {
  width: 100%;
  display: flex;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  border: none;
  background: var(--orange-300-main, #eb4c10);
  cursor: pointer;
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;

  &.disabled {
    background: var(--Gray-500, #949494);
    cursor: not-allowed;
  }
}

/* 추가해석 결제 팝업 (Figma 디자인) */
.extra-pay-popup {
  background: var(--gray-100-white, #fff);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 325px;

  &__text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    text-align: center;
    padding: 28px 16px;
    font-size: 15px;
    line-height: 24px;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-weight: 700;
  }

  &__desc {
    color: var(--Gray-800, #333);
    font-weight: 400;
  }

  &__price {
    color: var(--orange-300-main, #eb4c10);
    font-weight: 700;
  }

  &__buttons {
    display: flex;
    width: 100%;
  }

  &__btn {
    flex: 1;
    height: 48px;
    border: none;
    cursor: pointer;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;

    &--cancel {
      background: var(--Gray-200, #f8f8f8);
      color: var(--Gray-800, #333);
      border-radius: 0 0 0 16px;
    }

    &--confirm {
      background: var(--orange-300-main, #eb4c10);
      color: var(--gray-100-white, #fff);
      border-radius: 0 0 16px 0;
    }

    &--charge {
      background: var(--orange-300-main, #eb4c10);
      color: var(--gray-100-white, #fff);
      border-radius: 0 0 16px 0;
    }
  }
}

/* 월 선택 팝업 */
.month-select-popup {
  background: var(--gray-100-white, #fff);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 400px;
  padding-bottom: 32px;
  padding-top: 20px;
  gap: 12px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    height: 44px;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: center;
  }

  &__close {
    position: absolute;
    right: 16px;
    top: 10px;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    cursor: pointer;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 14px;
      height: 2px;
      background: var(--Gray-800, #333);
      border-radius: 1px;
    }

    &::before {
      transform: translate(-50%, -50%) rotate(45deg);
    }

    &::after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    width: 336px;
  }

  &__info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    background: var(--Gray-200, #f8f8f8);
    box-sizing: border-box;
  }

  &__info-label {
    color: var(--orange-300-main, #eb4c10);
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
  }

  &__info-point {
    color: var(--Gray-700, #575757);
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
  }

  &__options {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
  }

  &__option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 20px;
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    flex: 1;
    min-width: 0;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.2s;

    &.active {
      border-color: var(--orange-300-main, #eb4c10);
      background: var(--Orange-100, #fff8f5);
    }

    &.active &-label {
      color: var(--orange-300-main, #eb4c10);
      font-weight: 700;
    }

    &.active &-price {
      color: var(--orange-300-main, #eb4c10);
    }

    &.disabled {
      background: var(--Gray-200, #f8f8f8);
      border-color: transparent;
      cursor: default;
    }

    &.disabled &-label {
      color: var(--Gray-600, #757575);
      font-weight: 600;
    }
  }

  &__option-label {
    color: var(--Gray-800, #333);
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    text-align: center;
    white-space: nowrap;
  }

  &__option-price {
    color: var(--Gray-800, #333);
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
  }

  &__option-purchased {
    color: var(--Gray-600, #757575);
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
  }

  &__confirm {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 336px;
    padding: 14px 20px;
    border-radius: 6px;
    background: var(--orange-300-main, #eb4c10);
    color: var(--gray-100-white, #fff);
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    border: none;
    cursor: pointer;
    margin-top: 10px;

    &:hover {
      opacity: 0.9;
    }

    &--disabled {
      background: var(--Gray-500, #949494);
      cursor: default;

      &:hover {
        opacity: 1;
      }
    }
  }
}
</style>
